/**
 * Pure utility: resolves which boot-gate branch applies for the authenticated
 * user given their location list and the current localStorage cache.
 *
 * Used by _authenticated.tsx and unit-tested in isolation.
 *
 * Branch decision table (FR-18, FR-19, FR-17):
 *
 *   | Active count | Cached id valid? | Action      |
 *   |--------------|-----------------|-------------|
 *   | 0            | —               | block       |
 *   | 1            | —               | auto-select |
 *   | 2+           | yes             | proceed     |
 *   | 2+           | no              | show-picker |
 */

import type { AssignedLocationResponse } from '@jq4race/shared';

// Discriminated union — exhaustive switch is safe on `result.action`.
export type BootLocationResult =
  | { action: 'block' }
  | { action: 'auto-select'; locationId: string }
  | { action: 'proceed'; locationId: string }
  | { action: 'show-picker' };

/**
 * @param locations - Raw location list from `getMyLocations()` (may include inactive).
 * @param cachedId  - Current value of `localStorage.getItem('jq4race:active_location_id')`, or null.
 */
export function resolveBootLocation(
  locations: AssignedLocationResponse[],
  cachedId: string | null,
): BootLocationResult {
  const active = locations.filter((l) => l.status === 'active');

  if (active.length === 0) {
    return { action: 'block' };
  }

  if (active.length === 1 && active[0]) {
    return { action: 'auto-select', locationId: active[0].id };
  }

  // 2+ active locations
  const isValidCache = cachedId !== null && active.some((l) => l.id === cachedId);
  if (isValidCache) {
    return { action: 'proceed', locationId: cachedId };
  }

  return { action: 'show-picker' };
}
